import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { addMapboxBaseLayers } from "../helpers/mapbox_base_layers"

// Two-way map/marker <-> latitude/longitude field binding for the studio QR form.
// Fetches the selected loggable's geometry from the loggable_geometry endpoint,
// draws it as an overlay, and keeps a draggable marker synced with the fields.
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "loggable"]
  static values = { geometryUrl: String }

  connect() {
    this.initMap()
    this.loadGeometry({ recenterMarker: false })
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap() {
    // Default view: continental US, low zoom, until geometry/coords arrive.
    this.map = L.map(this.mapTarget).setView([39.8283, -98.5795], 4)

    addMapboxBaseLayers(this.map)

    this.map.on("click", (e) => this.setMarker(e.latlng.lat, e.latlng.lng))

    const lat = parseFloat(this.latitudeTarget.value)
    const lng = parseFloat(this.longitudeTarget.value)
    if (!isNaN(lat) && !isNaN(lng)) {
      this.setMarker(lat, lng, { write: false })
      this.map.setView([lat, lng], 13)
    }
  }

  // data-action: change->qr-code-map-picker#loggableChanged
  loggableChanged() {
    this.loadGeometry({ recenterMarker: true })
  }

  // data-action: input->qr-code-map-picker#coordinatesChanged
  coordinatesChanged() {
    const lat = parseFloat(this.latitudeTarget.value)
    const lng = parseFloat(this.longitudeTarget.value)
    if (isNaN(lat) || isNaN(lng)) return
    this.setMarker(lat, lng, { write: false })
    this.map.panTo([lat, lng])
  }

  async loadGeometry({ recenterMarker }) {
    const gid = this.loggableTarget.value
    if (!gid) return

    const token = (this.loadToken = Symbol())

    let data
    try {
      const url = `${this.geometryUrlValue}?gid=${encodeURIComponent(gid)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) return
      data = await response.json()
    } catch (e) {
      console.warn("Failed to load loggable geometry", e)
      return
    }

    // A newer load (e.g. the user changed the select again) superseded this one.
    if (this.loadToken !== token) return

    if (this.geometryLayer) {
      this.map.removeLayer(this.geometryLayer)
      this.geometryLayer = null
    }

    let center = null
    if (data.geojson) {
      this.geometryLayer = L.geoJSON(data.geojson).addTo(this.map)
      const bounds = this.geometryLayer.getBounds()
      if (bounds.isValid()) {
        center = bounds.getCenter() // before extend(), so it's the geometry's true center
        if (this.marker) {
          this.map.fitBounds(bounds.extend(this.marker.getLatLng()))
        } else {
          this.map.fitBounds(bounds)
        }
      }
    } else if (data.center && data.center[0] != null && data.center[1] != null) {
      center = L.latLng(data.center[0], data.center[1])
      this.map.setView(center, 13)
    }

    const hasCoords = this.latitudeTarget.value && this.longitudeTarget.value
    if (center && (recenterMarker || !hasCoords)) {
      this.setMarker(center.lat, center.lng)
    }
  }

  setMarker(lat, lng, { write = true } = {}) {
    if (this.marker) {
      this.marker.setLatLng([lat, lng])
    } else {
      this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map)
      this.marker.on("dragend", () => {
        const pos = this.marker.getLatLng()
        this.writeFields(pos.lat, pos.lng)
      })
    }
    if (write) this.writeFields(lat, lng)
  }

  writeFields(lat, lng) {
    this.latitudeTarget.value = Number(lat).toFixed(6)
    this.longitudeTarget.value = Number(lng).toFixed(6)
  }
}
